// frontend\src\components\InputDialog.tsx
// Create / edit an input, its usage rules, and draft a reorder email.
import React, { useState, useEffect, useRef, useCallback } from 'react';
import AddTaskButton from './tasks/AddTaskButton';
import { useToast } from '../context/ToastContext';
import api from '../utils/api';
import { Input, InputUsageRule, InputAdjustment, StockItem, Supplier } from '../types';
import InputUsageRuleBuilder from './InputUsageRuleBuilder';
import '../OrderDetailsDialog.css';
import '../core.css';
import '../inputs.css';

interface InputDialogProps {
  input: Input | null;
  isNew: boolean;
  onClose: () => void;
}

interface FormState {
  name: string;
  part_number: string;
  cost: string;
  cost_currency: string;
  supplier_name: string;
  supplier_email: string;
  supplier_url: string;
  delivery_days: string;
  safety_days: string;
  reorder_qty: string;
  stock_baseline: string;
  notes: string;
  archived: boolean;
  warehouse_ids: number[];
}

const emptyForm: FormState = {
  name: '',
  part_number: '',
  cost: '',
  cost_currency: 'USD',
  supplier_name: '',
  supplier_email: '',
  supplier_url: '',
  delivery_days: '0',
  safety_days: '0',
  reorder_qty: '',
  stock_baseline: '0',
  notes: '',
  archived: false,
  warehouse_ids: [],
};

const toForm = (input: Input): FormState => ({
  name: input.name ?? '',
  part_number: input.part_number ?? '',
  cost: input.cost === null || input.cost === undefined ? '' : String(input.cost),
  cost_currency: input.cost_currency ?? 'USD',
  supplier_name: input.supplier_name ?? '',
  supplier_email: input.supplier_email ?? '',
  supplier_url: input.supplier_url ?? '',
  delivery_days: String(input.delivery_days ?? 0),
  safety_days: String(input.safety_days ?? 0),
  reorder_qty: input.reorder_qty === null || input.reorder_qty === undefined ? '' : String(input.reorder_qty),
  stock_baseline: String(input.stock_baseline ?? 0),
  notes: input.notes ?? '',
  archived: input.archived ?? false,
  warehouse_ids: input.warehouse_ids ?? [],
});

const InputDialog: React.FC<InputDialogProps> = ({ input, isNew, onClose }) => {
  const { showToast } = useToast();

  const [form, setForm] = useState<FormState>(isNew || !input ? emptyForm : toForm(input));
  const [rules, setRules] = useState<InputUsageRule[]>(input?.rules ?? []);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [warehouses, setWarehouses] = useState<{ id: number; name: string }[]>([]);
  const [stockData, setStockData] = useState<StockItem[]>([]);
  const [adjustments, setAdjustments] = useState<InputAdjustment[]>([]);
  const [adjustQty, setAdjustQty] = useState('');
  const [adjustReason, setAdjustReason] = useState('');

  const [saving, setSaving] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showEmailTemplate, setShowEmailTemplate] = useState(false);
  const [emailTemplate, setEmailTemplate] = useState('');

  const dialogRef = useRef<HTMLDivElement>(null);
  const initialSnapshotRef = useRef<string>('');

  const makeSnapshot = useCallback(
    (f: FormState, r: InputUsageRule[]) =>
      JSON.stringify({
        f,
        r: r.map((rule) => ({
          qty_per_unit: rule.qty_per_unit,
          scope: rule.scope,
          supplier_name: rule.supplier_name ?? null,
          product_id: rule.product_id ?? null,
          variation_id: rule.variation_id ?? 0,
        })),
      }),
    []
  );

  useEffect(() => {
    initialSnapshotRef.current = makeSnapshot(
      isNew || !input ? emptyForm : toForm(input),
      input?.rules ?? []
    );
  }, [input, isNew, makeSnapshot]);

  // The rule builder needs supplier and product options. /neon-stock returns
  // both, and is what PurchaseOrderDialog uses for the same purpose.
  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const response = await api.get('/neon-stock');
        setSuppliers(response.data.suppliers || []);
        setStockData(response.data.data || []);
      } catch (error) {
        console.error('Failed to fetch rule options:', error);
        showToast('Failed to load product and supplier options', 'error');
      }
    };
    fetchOptions();
  }, [showToast]);

  useEffect(() => {
    api
      .get('/warehouses')
      .then((response) => setWarehouses(response.data.warehouses || []))
      .catch((error) => console.error('Failed to fetch warehouses:', error));
  }, []);

  useEffect(() => {
    if (isNew || !input) return;
    const fetchAdjustments = async () => {
      try {
        const response = await api.get(`/input-adjustments?input_id=${input.id}`);
        setAdjustments(response.data.adjustments || []);
      } catch (error) {
        console.error('Failed to fetch adjustments:', error);
      }
    };
    fetchAdjustments();
  }, [input, isNew]);

  const handleClose = useCallback(() => {
    const current = makeSnapshot(form, rules);
    if (current !== initialSnapshotRef.current) {
      if (!window.confirm('You have unsaved changes. Close without saving?')) return;
    }
    onClose();
  }, [form, rules, makeSnapshot, onClose]);

  // Close on Escape, unless a nested overlay is open.
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !showDeleteConfirm && !showEmailTemplate) {
        handleClose();
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleClose, showDeleteConfirm, showEmailTemplate]);

  const setField = (field: keyof FormState, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const toggleWarehouse = (id: number, on: boolean) =>
    setForm((prev) => ({
      ...prev,
      warehouse_ids: on ? [...prev.warehouse_ids.filter((w) => w !== id), id] : prev.warehouse_ids.filter((w) => w !== id),
    }));

  const handleSave = async () => {
    if (!form.name.trim()) {
      showToast('Name is required', 'error');
      return;
    }
    for (const rule of rules) {
      if (!(Number(rule.qty_per_unit) > 0)) {
        showToast('Every rule needs a quantity greater than zero', 'error');
        return;
      }
      if (rule.scope === 'supplier' && !rule.supplier_name) {
        showToast('Every supplier rule needs a supplier selected', 'error');
        return;
      }
      if (rule.scope === 'product' && !rule.product_id) {
        showToast('Every product rule needs a product selected', 'error');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        ...(isNew ? {} : { id: input?.id }),
        name: form.name.trim(),
        part_number: form.part_number.trim() || null,
        cost: form.cost === '' ? null : Number(form.cost),
        cost_currency: form.cost_currency,
        supplier_name: form.supplier_name.trim() || null,
        supplier_email: form.supplier_email.trim() || null,
        supplier_url: form.supplier_url.trim() || null,
        delivery_days: Number(form.delivery_days) || 0,
        safety_days: Number(form.safety_days) || 0,
        reorder_qty: form.reorder_qty === '' ? null : Number(form.reorder_qty),
        stock_baseline: Number(form.stock_baseline) || 0,
        notes: form.notes.trim() || null,
        archived: form.archived,
        warehouse_ids: form.warehouse_ids,
        rules: rules.map((rule) => ({
          qty_per_unit: Number(rule.qty_per_unit),
          scope: rule.scope,
          supplier_name: rule.supplier_name ?? null,
          product_id: rule.product_id ?? null,
          variation_id: rule.variation_id ?? 0,
        })),
      };

      if (isNew) {
        await api.post('/inputs', payload);
        showToast('Input created successfully', 'success');
      } else {
        await api.put('/inputs', payload);
        showToast('Input updated successfully', 'success');
      }

      initialSnapshotRef.current = makeSnapshot(form, rules);
      onClose();
    } catch (error: any) {
      console.error('Failed to save input:', error);
      showToast(error?.response?.data?.error || 'Failed to save input', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!input?.id) return;
    setSaving(true);
    try {
      await api.delete(`/inputs?id=${input.id}`);
      showToast('Input deleted successfully', 'success');
      initialSnapshotRef.current = makeSnapshot(form, rules);
      onClose();
    } catch (error) {
      console.error('Failed to delete input:', error);
      showToast('Failed to delete input', 'error');
    } finally {
      setSaving(false);
      setShowDeleteConfirm(false);
    }
  };

  const handleAddAdjustment = async () => {
    if (!input?.id) return;
    const qty = Number(adjustQty);
    if (!Number.isFinite(qty) || qty === 0) {
      showToast('Enter a non-zero quantity', 'error');
      return;
    }
    try {
      const response = await api.post('/input-adjustments', {
        input_id: input.id,
        quantity: qty,
        reason: adjustReason.trim() || null,
      });
      setAdjustments((prev) => [response.data.adjustment, ...prev]);
      setAdjustQty('');
      setAdjustReason('');
      showToast('Adjustment recorded', 'success');
    } catch (error) {
      console.error('Failed to record adjustment:', error);
      showToast('Failed to record adjustment', 'error');
    }
  };

  // Default to roughly two months of cover when no reorder quantity is set.
  const suggestedOrderQty = () => {
    if (form.reorder_qty !== '') return Number(form.reorder_qty);
    const burn = input?.burn_rate ?? 0;
    return burn > 0 ? Math.ceil(burn * 2) : 0;
  };

  const generateEmailTemplate = () => {
    const qty = suggestedOrderQty();
    const partLine = form.part_number ? `Part number: ${form.part_number}\n` : '';
    const stockLine = input
      ? `\nOur current stock is ${input.current_stock} and we use roughly ${input.burn_rate} per month.`
      : '';

    return `To: ${form.supplier_email || '(no supplier email on file)'}
Subject: Reorder — ${form.name}${form.part_number ? ` (${form.part_number})` : ''}

Hi,

We would like to place an order for the following:

${qty > 0 ? `${qty}x ` : ''}${form.name}
${partLine}
Please confirm pricing, availability and estimated ship date.${stockLine}

Thank you,
Mountain Pass Performance`;
  };

  const handleOrderMore = () => {
    setEmailTemplate(generateEmailTemplate());
    setShowEmailTemplate(true);
  };

  return (
    <div
      className="order-dialog-overlay"
      onMouseDown={(e) => {
        if (dialogRef.current && !dialogRef.current.contains(e.target as Node)) {
          if (!showDeleteConfirm && !showEmailTemplate) handleClose();
        }
      }}
    >
      <div className="order-dialog" ref={dialogRef}>
        <button className="close-button" onClick={handleClose}>
          ✕
        </button>

        {!isNew && input && (
          <AddTaskButton className="tk-add-task-dialog" link={{ link_type: 'input', link_ref: String(input.id), link_label: input.name }} />
        )}

        <h2>{isNew ? 'New Input' : form.name || 'Input'}</h2>

        {!isNew && input && (
          <div className="input-dialog-summary">
            <div>
              <span className="input-summary-label">Stock</span>
              <span className="input-summary-value">{input.current_stock}</span>
            </div>
            <div>
              <span className="input-summary-label">Burn / month</span>
              <span className="input-summary-value">
                {input.burn_rate}
                <span className="input-summary-note">
                  {input.burn_source === 'forecast' ? ' est.' : ''}
                </span>
              </span>
            </div>
            <div>
              <span className="input-summary-label">Days cover</span>
              <span className="input-summary-value">
                {input.days_cover === null ? '—' : input.days_cover}
              </span>
            </div>
            <div>
              <span className="input-summary-label">Order by</span>
              <span className="input-summary-value">{input.reorder_by ?? '—'}</span>
            </div>
          </div>
        )}

        <h3 className="section-heading">Details</h3>
        <div className="input-form-grid">
          <label>
            Name
            <input
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="Thank you card"
            />
          </label>
          <label>
            Part Number
            <input
              type="text"
              value={form.part_number}
              onChange={(e) => setField('part_number', e.target.value)}
            />
          </label>
          <label>
            Cost (each)
            <input
              type="number"
              step="0.01"
              min="0"
              value={form.cost}
              onChange={(e) => setField('cost', e.target.value)}
            />
          </label>
          <label>
            Currency
            <select
              value={form.cost_currency}
              onChange={(e) => setField('cost_currency', e.target.value)}
            >
              <option value="USD">USD</option>
              <option value="CAD">CAD</option>
              <option value="EUR">EUR</option>
            </select>
          </label>
          <label>
            Delivery Time (days)
            <input
              type="number"
              min="0"
              value={form.delivery_days}
              onChange={(e) => setField('delivery_days', e.target.value)}
            />
          </label>
          <label>
            Safety Buffer (days)
            <input
              type="number"
              min="0"
              value={form.safety_days}
              onChange={(e) => setField('safety_days', e.target.value)}
            />
          </label>
          <label>
            Stock (manual count)
            <input
              type="number"
              value={form.stock_baseline}
              onChange={(e) => setField('stock_baseline', e.target.value)}
            />
          </label>
          <label>
            Reorder Quantity
            <input
              type="number"
              min="0"
              value={form.reorder_qty}
              onChange={(e) => setField('reorder_qty', e.target.value)}
            />
          </label>
        </div>

        {!isNew && Number(form.stock_baseline) !== (input?.stock_baseline ?? 0) && (
          <p className="input-recount-warning">
            Changing Stock records a new count as of now. Consumption before this point will
            no longer be subtracted.
          </p>
        )}

        <h3 className="section-heading">Supplier</h3>
        <div className="input-form-grid">
          <label>
            Supplier Name
            <input
              type="text"
              value={form.supplier_name}
              onChange={(e) => setField('supplier_name', e.target.value)}
            />
          </label>
          <label>
            Supplier Email
            <input
              type="email"
              value={form.supplier_email}
              onChange={(e) => setField('supplier_email', e.target.value)}
            />
          </label>
          <label className="input-form-wide">
            Order Link
            <input
              type="url"
              value={form.supplier_url}
              onChange={(e) => setField('supplier_url', e.target.value)}
              placeholder="https://"
            />
          </label>
        </div>

        <div className="input-order-actions">
          <button className="action-button" onClick={handleOrderMore}>
            Order More
          </button>
          {form.supplier_url && (
            <a
              className="action-button"
              href={form.supplier_url}
              target="_blank"
              rel="noreferrer"
            >
              Open Supplier Site
            </a>
          )}
        </div>

        <h3 className="section-heading">Usage</h3>
        <InputUsageRuleBuilder
          rules={rules}
          onChange={setRules}
          suppliers={suppliers}
          stockData={stockData}
        />

        <h3 className="section-heading">Used By</h3>
        <p className="input-rules-hint">
          Warehouses that use this input. Their users see its stock on their Inputs page and can request more.
        </p>
        <div className="input-used-by">
          {warehouses.length === 0 && <span className="input-rules-hint">Loading warehouses…</span>}
          {warehouses.map((w) => (
            <label key={w.id} className="checkbox-label">
              <input
                type="checkbox"
                checked={form.warehouse_ids.includes(w.id)}
                onChange={(e) => toggleWarehouse(w.id, e.target.checked)}
              />
              {w.name}
            </label>
          ))}
        </div>

        {!isNew && input && (
          <>
            <h3 className="section-heading">Stock Adjustments</h3>
            <p className="input-rules-hint">
              Record a delivery (positive) or a write-off (negative) without redoing a full
              count.
            </p>
            <div className="input-adjust-row">
              <input
                type="number"
                className="input-adjust-qty"
                placeholder="Qty"
                value={adjustQty}
                onChange={(e) => setAdjustQty(e.target.value)}
              />
              <input
                type="text"
                className="input-adjust-reason"
                placeholder="Reason (e.g. delivery received)"
                value={adjustReason}
                onChange={(e) => setAdjustReason(e.target.value)}
              />
              <button className="action-button" onClick={handleAddAdjustment}>
                Record
              </button>
            </div>
            {adjustments.length > 0 && (
              <ul className="input-adjust-list">
                {adjustments.slice(0, 8).map((adjustment) => (
                  <li key={adjustment.id}>
                    <span className={adjustment.quantity >= 0 ? 'input-adjust-add' : 'input-adjust-remove'}>
                      {adjustment.quantity >= 0 ? '+' : ''}
                      {adjustment.quantity}
                    </span>
                    <span>{adjustment.reason || 'No reason given'}</span>
                    <span className="input-adjust-meta">
                      {new Date(adjustment.created_at).toLocaleDateString()} ·{' '}
                      {adjustment.created_by || 'unknown'}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        <h3 className="section-heading">Notes</h3>
        <textarea
          className="address-textarea"
          value={form.notes}
          onChange={(e) => setField('notes', e.target.value)}
          rows={3}
        />

        {!isNew && (
          <label className="checkbox-label input-archive-toggle">
            <input
              type="checkbox"
              checked={form.archived}
              onChange={(e) => setField('archived', e.target.checked)}
            />
            Archived (hidden from the list, stops accruing consumption)
          </label>
        )}

        <div className="order-dialog-actions">
          {!isNew && (
            <button
              className="action-button delete-button"
              onClick={() => setShowDeleteConfirm(true)}
              disabled={saving}
            >
              Delete
            </button>
          )}
          <button className="action-button cancel-button" onClick={handleClose} disabled={saving}>
            Cancel
          </button>
          <button
            className="action-button primary-button"
            onClick={handleSave}
            disabled={saving || !form.name.trim()}
          >
            {saving ? 'Saving...' : isNew ? 'Create Input' : 'Save'}
          </button>
        </div>

        {showDeleteConfirm && (
          <div className="supplier-dialog-overlay">
            <div className="supplier-dialog">
              <h3>Delete Input</h3>
              <p>
                Delete "{form.name}"? Its usage rules and consumption history will be removed
                permanently. Archive it instead if you only want it off the list.
              </p>
              <div className="supplier-dialog-actions">
                <button
                  className="action-button cancel-button"
                  onClick={() => setShowDeleteConfirm(false)}
                >
                  Cancel
                </button>
                <button className="action-button delete-button" onClick={handleDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}

        {showEmailTemplate && (
          <div className="supplier-dialog-overlay">
            <div className="email-template-dialog">
              <h3>Reorder Email</h3>
              <div className="email-template-content inputs-email-content">
                <pre>{emailTemplate}</pre>
              </div>
              <div className="supplier-dialog-actions">
                <button
                  className="action-button cancel-button"
                  onClick={() => setShowEmailTemplate(false)}
                >
                  Close
                </button>
                <button
                  className="action-button"
                  onClick={() => {
                    navigator.clipboard.writeText(emailTemplate);
                    showToast('Reorder email copied to clipboard', 'success');
                  }}
                >
                  Copy to Clipboard
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default InputDialog;
